/**
 * NatalChart.jsx - Professional birth chart visualization
 * 
 * Features:
 * - Professional SVG chart wheel matching astro-seek.com style
 * - Outer zodiac ring with sign symbols and degree markers
 * - House divisions with numbered cusps
 * - Planet positions with symbols and degrees
 * - Aspect lines (blue=harmonious, red=challenging)
 * - Planet table with sign, degree, house
 * - Element/Modality balance
 * - Share and download functionality
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, Download, Share2, MessageCircle, ChevronDown, ChevronUp, 
    Info, RefreshCw, Eye, EyeOff, Star
} from 'lucide-react';
import { getAuthHeaders } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';
import SocialShareModal from './SocialShareModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Zodiac sign data with symbols
const ZODIAC_SIGNS = [
    { name: 'Aries', symbol: '♈', glyph: '♈', element: 'Fire', color: '#EF4444' },
    { name: 'Taurus', symbol: '♉', glyph: '♉', element: 'Earth', color: '#84CC16' },
    { name: 'Gemini', symbol: '♊', glyph: '♊', element: 'Air', color: '#60A5FA' },
    { name: 'Cancer', symbol: '♋', glyph: '♋', element: 'Water', color: '#06B6D4' },
    { name: 'Leo', symbol: '♌', glyph: '♌', element: 'Fire', color: '#F59E0B' },
    { name: 'Virgo', symbol: '♍', glyph: '♍', element: 'Earth', color: '#84CC16' },
    { name: 'Libra', symbol: '♎', glyph: '♎', element: 'Air', color: '#60A5FA' },
    { name: 'Scorpio', symbol: '♏', glyph: '♏', element: 'Water', color: '#06B6D4' },
    { name: 'Sagittarius', symbol: '♐', glyph: '♐', element: 'Fire', color: '#EF4444' },
    { name: 'Capricorn', symbol: '♑', glyph: '♑', element: 'Earth', color: '#84CC16' },
    { name: 'Aquarius', symbol: '♒', glyph: '♒', element: 'Air', color: '#60A5FA' },
    { name: 'Pisces', symbol: '♓', glyph: '♓', element: 'Water', color: '#06B6D4' }
];

// Planet data
const PLANET_DATA = {
    Sun: { symbol: '☉', color: '#FFD700', name: { en: 'Sun', es: 'Sol' } },
    Moon: { symbol: '☽', color: '#C0C0C0', name: { en: 'Moon', es: 'Luna' } },
    Mercury: { symbol: '☿', color: '#A78BFA', name: { en: 'Mercury', es: 'Mercurio' } },
    Venus: { symbol: '♀', color: '#EC4899', name: { en: 'Venus', es: 'Venus' } },
    Mars: { symbol: '♂', color: '#EF4444', name: { en: 'Mars', es: 'Marte' } },
    Jupiter: { symbol: '♃', color: '#F59E0B', name: { en: 'Jupiter', es: 'Júpiter' } },
    Saturn: { symbol: '♄', color: '#6B7280', name: { en: 'Saturn', es: 'Saturno' } },
    Uranus: { symbol: '♅', color: '#06B6D4', name: { en: 'Uranus', es: 'Urano' } },
    Neptune: { symbol: '♆', color: '#3B82F6', name: { en: 'Neptune', es: 'Neptuno' } },
    Pluto: { symbol: '♇', color: '#7C3AED', name: { en: 'Pluto', es: 'Plutón' } },
    'North Node': { symbol: '☊', color: '#9333EA', name: { en: 'North Node', es: 'Nodo Norte' } },
    Chiron: { symbol: '⚷', color: '#14B8A6', name: { en: 'Chiron', es: 'Quirón' } },
    Lilith: { symbol: '⚸', color: '#1F2937', name: { en: 'Lilith', es: 'Lilith' } }
};

// Aspect configurations
const ASPECT_CONFIG = {
    conjunction: { degrees: 0, orb: 8, color: '#D4AF37', harmonious: true },
    sextile: { degrees: 60, orb: 6, color: '#3B82F6', harmonious: true },
    square: { degrees: 90, orb: 8, color: '#EF4444', harmonious: false },
    trine: { degrees: 120, orb: 8, color: '#3B82F6', harmonious: true },
    opposition: { degrees: 180, orb: 8, color: '#EF4444', harmonious: false }
};

// Element colors
const ELEMENT_COLORS = {
    Fire: '#EF4444',
    Earth: '#84CC16', 
    Air: '#60A5FA',
    Water: '#06B6D4'
};

// Professional Chart Wheel Component - Dark Theme
const ProfessionalChartWheel = ({ chartData, showAspects, size = 400 }) => {
    if (!chartData?.planets) return null;
    
    const center = size / 2;
    const outerRadius = size / 2 - 8;
    const zodiacOuterRadius = outerRadius;
    const zodiacInnerRadius = outerRadius - 40;
    const houseOuterRadius = zodiacInnerRadius - 3;
    const houseInnerRadius = houseOuterRadius - 90;
    const planetRadius = houseInnerRadius + 45;
    const aspectRadius = houseInnerRadius - 15;
    const innerCircleRadius = 35;
    
    // Dark theme colors
    const THEME = {
        bg: '#0a0a0f',
        bgGradient: 'url(#chartBgGradient)',
        zodiacRing: '#15151f',
        border: '#2a2a3a',
        borderGold: '#d4af37',
        text: '#888899',
        textBright: '#aaaabc',
        glow: 'rgba(212, 175, 55, 0.3)'
    };
    
    // Convert zodiac longitude to SVG angle (0° Aries = left/9 o'clock, counter-clockwise)
    const longitudeToAngle = (longitude) => {
        return 180 - longitude;
    };
    
    // Get X,Y coordinates from angle and radius
    const getPoint = (angleDeg, radius) => {
        const angleRad = (angleDeg * Math.PI) / 180;
        return {
            x: center + radius * Math.cos(angleRad),
            y: center - radius * Math.sin(angleRad)
        };
    };
    
    // Create arc path
    const createArc = (startAngle, endAngle, radius) => {
        const start = getPoint(startAngle, radius);
        const end = getPoint(endAngle, radius);
        const largeArc = Math.abs(startAngle - endAngle) > 180 ? 1 : 0;
        const sweep = startAngle > endAngle ? 1 : 0;
        return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${end.x} ${end.y}`;
    };
    
    // House cusps (use actual data or default to equal houses)
    const houses = chartData.houses || Array.from({ length: 12 }, (_, i) => ({ 
        longitude: (i * 30), 
        sign: ZODIAC_SIGNS[i].name 
    }));
    
    // Planets array
    const planets = Object.entries(chartData.planets || {}).map(([name, data]) => ({
        name,
        longitude: data.longitude || 0,
        sign: data.sign,
        degree: Math.floor((data.longitude || 0) % 30),
        minute: Math.floor(((data.longitude || 0) % 1) * 60),
        house: data.house,
        retrograde: data.retrograde,
        ...(PLANET_DATA[name] || { symbol: '?', color: '#999' })
    })).filter(p => p.longitude !== undefined);
    
    // Calculate aspects
    const aspects = [];
    if (showAspects && chartData.aspects) {
        chartData.aspects.forEach(asp => {
            const config = ASPECT_CONFIG[asp.type];
            if (config) {
                aspects.push({ ...asp, ...config });
            }
        });
    }
    
    return (
        <svg 
            width={size} 
            height={size} 
            viewBox={`0 0 ${size} ${size}`}
            className="mx-auto"
            style={{ borderRadius: '50%' }}
        >
            {/* Definitions */}
            <defs>
                {/* Background gradient */}
                <radialGradient id="chartBgGradient" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#12121a" />
                    <stop offset="70%" stopColor="#08080c" />
                    <stop offset="100%" stopColor="#040406" />
                </radialGradient>
                
                {/* Golden glow filter */}
                <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
                
                {/* Planet glow filter */}
                <filter id="planetGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                    <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            
            {/* Background circle */}
            <circle cx={center} cy={center} r={outerRadius + 5} fill={THEME.bgGradient} />
            
            {/* Outer golden border */}
            <circle 
                cx={center} 
                cy={center} 
                r={outerRadius} 
                fill="none" 
                stroke={THEME.borderGold}
                strokeWidth="2"
                opacity="0.6"
                filter="url(#goldGlow)"
            />
            
            {/* Zodiac Ring Background */}
            <circle cx={center} cy={center} r={zodiacOuterRadius - 1} fill={THEME.zodiacRing} />
            <circle cx={center} cy={center} r={zodiacInnerRadius + 1} fill={THEME.bg} />
            
            {/* Zodiac Signs - 30° each */}
            {ZODIAC_SIGNS.map((sign, i) => {
                const startAngle = 180 - (i * 30);
                const endAngle = startAngle - 30;
                const midAngle = startAngle - 15;
                const symbolPos = getPoint(midAngle, zodiacOuterRadius - 20);
                
                // Degree markers every 5°
                const markers = [];
                for (let d = 0; d < 30; d += 5) {
                    const markerAngle = startAngle - d;
                    const markerLength = d === 0 ? 10 : (d % 10 === 0 ? 6 : 3);
                    const inner = getPoint(markerAngle, zodiacInnerRadius);
                    const outer = getPoint(markerAngle, zodiacInnerRadius + markerLength);
                    markers.push(
                        <line
                            key={`marker-${i}-${d}`}
                            x1={inner.x}
                            y1={inner.y}
                            x2={outer.x}
                            y2={outer.y}
                            stroke={d === 0 ? THEME.borderGold : '#3a3a4a'}
                            strokeWidth={d === 0 ? "1.5" : "0.5"}
                            opacity={d === 0 ? 0.7 : 0.5}
                        />
                    );
                }
                
                return (
                    <g key={sign.name}>
                        {/* Sign segment subtle background */}
                        <path
                            d={`M ${getPoint(startAngle, zodiacInnerRadius + 2).x} ${getPoint(startAngle, zodiacInnerRadius + 2).y}
                                A ${zodiacInnerRadius + 2} ${zodiacInnerRadius + 2} 0 0 0 ${getPoint(endAngle, zodiacInnerRadius + 2).x} ${getPoint(endAngle, zodiacInnerRadius + 2).y}
                                L ${getPoint(endAngle, zodiacOuterRadius - 2).x} ${getPoint(endAngle, zodiacOuterRadius - 2).y}
                                A ${zodiacOuterRadius - 2} ${zodiacOuterRadius - 2} 0 0 1 ${getPoint(startAngle, zodiacOuterRadius - 2).x} ${getPoint(startAngle, zodiacOuterRadius - 2).y} Z`}
                            fill={sign.color}
                            opacity="0.03"
                        />
                        {/* Sign divider line */}
                        <line
                            x1={getPoint(startAngle, zodiacInnerRadius).x}
                            y1={getPoint(startAngle, zodiacInnerRadius).y}
                            x2={getPoint(startAngle, zodiacOuterRadius).x}
                            y2={getPoint(startAngle, zodiacOuterRadius).y}
                            stroke={THEME.border}
                            strokeWidth="1"
                        />
                        {/* Degree markers */}
                        {markers}
                        {/* Sign symbol */}
                        <text
                            x={symbolPos.x}
                            y={symbolPos.y}
                            fill={sign.color}
                            fontSize="18"
                            fontFamily="serif"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ fontWeight: 'bold', filter: 'url(#planetGlow)' }}
                        >
                            {sign.glyph}
                        </text>
                    </g>
                );
            })}
            
            {/* House ring borders */}
            <circle cx={center} cy={center} r={zodiacInnerRadius} fill="none" stroke={THEME.border} strokeWidth="1.5" />
            <circle cx={center} cy={center} r={houseInnerRadius} fill="none" stroke={THEME.border} strokeWidth="1" opacity="0.5" />
            
            {/* House divisions and numbers */}
            {houses.map((house, i) => {
                const angle = longitudeToAngle(house.longitude || (i * 30));
                const innerPoint = getPoint(angle, houseInnerRadius);
                const outerPoint = getPoint(angle, zodiacInnerRadius);
                
                // House number position (middle of house)
                const nextAngle = longitudeToAngle(houses[(i + 1) % 12]?.longitude || ((i + 1) * 30));
                const midAngle = angle - ((angle - nextAngle + 360) % 360) / 2;
                const numberPos = getPoint(midAngle > 0 ? midAngle : midAngle + 360, houseInnerRadius + 25);
                
                // ASC (1st), IC (4th), DSC (7th), MC (10th) are emphasized
                const isAngular = i === 0 || i === 3 || i === 6 || i === 9;
                const angleLabels = { 0: 'AC', 3: 'IC', 6: 'DC', 9: 'MC' };
                
                return (
                    <g key={`house-${i}`}>
                        {/* House cusp line */}
                        <line
                            x1={innerPoint.x}
                            y1={innerPoint.y}
                            x2={outerPoint.x}
                            y2={outerPoint.y}
                            stroke={isAngular ? THEME.borderGold : THEME.border}
                            strokeWidth={isAngular ? '2.5' : '1'}
                            opacity={isAngular ? 0.8 : 0.4}
                            filter={isAngular ? 'url(#goldGlow)' : 'none'}
                        />
                        {/* House number */}
                        <text
                            x={numberPos.x}
                            y={numberPos.y}
                            fill={THEME.text}
                            fontSize="11"
                            fontFamily="sans-serif"
                            textAnchor="middle"
                            dominantBaseline="middle"
                        >
                            {i + 1}
                        </text>
                        {/* Angular label (AC, IC, DC, MC) */}
                        {isAngular && (
                            <text
                                x={getPoint(angle, houseInnerRadius - 18).x}
                                y={getPoint(angle, houseInnerRadius - 18).y}
                                fill={THEME.borderGold}
                                fontSize="10"
                                fontWeight="bold"
                                fontFamily="sans-serif"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                filter="url(#goldGlow)"
                            >
                                {angleLabels[i]}
                            </text>
                        )}
                    </g>
                );
            })}
            
            {/* Aspect lines */}
            {aspects.map((aspect, i) => {
                const planet1 = planets.find(p => p.name === aspect.planet1);
                const planet2 = planets.find(p => p.name === aspect.planet2);
                if (!planet1 || !planet2) return null;
                
                const angle1 = longitudeToAngle(planet1.longitude);
                const angle2 = longitudeToAngle(planet2.longitude);
                const pos1 = getPoint(angle1, aspectRadius);
                const pos2 = getPoint(angle2, aspectRadius);
                
                return (
                    <line
                        key={`aspect-${i}`}
                        x1={pos1.x}
                        y1={pos1.y}
                        x2={pos2.x}
                        y2={pos2.y}
                        stroke={aspect.color}
                        strokeWidth="1.5"
                        opacity="0.5"
                        strokeDasharray={aspect.harmonious ? 'none' : '4,3'}
                    />
                );
            })}
            
            {/* Planet symbols */}
            {planets.map((planet) => {
                const angle = longitudeToAngle(planet.longitude);
                const pos = getPoint(angle, planetRadius);
                
                return (
                    <g key={planet.name} filter="url(#planetGlow)">
                        {/* Planet symbol background */}
                        <circle
                            cx={pos.x}
                            cy={pos.y}
                            r="14"
                            fill={THEME.bg}
                            stroke={planet.color}
                            strokeWidth="2"
                        />
                        {/* Planet symbol */}
                        <text
                            x={pos.x}
                            y={pos.y}
                            fill={planet.color}
                            fontSize="14"
                            fontFamily="serif"
                            textAnchor="middle"
                            dominantBaseline="middle"
                            style={{ fontWeight: 'bold' }}
                        >
                            {planet.symbol}
                        </text>
                        {/* Retrograde indicator */}
                        {planet.retrograde && (
                            <text
                                x={pos.x + 12}
                                y={pos.y - 12}
                                fill="#EF4444"
                                fontSize="9"
                                fontWeight="bold"
                            >
                                R
                            </text>
                        )}
                        {/* Degree display below planet */}
                        <text
                            x={pos.x}
                            y={pos.y + 22}
                            fill={THEME.textBright}
                            fontSize="8"
                            fontFamily="sans-serif"
                            textAnchor="middle"
                        >
                            {planet.degree}°
                        </text>
                    </g>
                );
            })}
            
            {/* Center circle with gradient */}
            <circle cx={center} cy={center} r={innerCircleRadius} fill={THEME.bgGradient} stroke={THEME.borderGold} strokeWidth="1.5" opacity="0.6" />
            <text
                x={center}
                y={center}
                fill={THEME.borderGold}
                fontSize="20"
                textAnchor="middle"
                dominantBaseline="middle"
                opacity="0.7"
                filter="url(#goldGlow)"
            >
                ✧
            </text>
        </svg>
    );
};

// Planet row component
const PlanetRow = ({ planet, data, isSpanish }) => {
    const planetInfo = PLANET_DATA[planet] || { symbol: '?', color: '#999', name: { en: planet, es: planet } };
    const signData = ZODIAC_SIGNS.find(s => s.name === data.sign) || ZODIAC_SIGNS[0];
    const degree = Math.floor((data.longitude || 0) % 30);
    const minute = Math.floor(((data.longitude || 0) % 1) * 60);
    
    return (
        <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
            <div className="flex items-center gap-3">
                <span style={{ color: planetInfo.color }} className="text-xl w-7 text-center font-serif">
                    {planetInfo.symbol}
                </span>
                <span className="text-sm text-white/80 font-inter">
                    {isSpanish ? planetInfo.name?.es : planetInfo.name?.en}
                </span>
                {data.retrograde && <span className="text-red-400 text-[10px] font-bold">R</span>}
            </div>
            <div className="flex items-center gap-4">
                <span className="text-sm text-white/60 font-mono">
                    {degree}°{minute.toString().padStart(2, '0')}'
                </span>
                <div className="flex items-center gap-1">
                    <span style={{ color: signData?.color }} className="text-base font-serif">
                        {signData?.symbol}
                    </span>
                    <span className="text-xs text-white/50">{data.sign}</span>
                </div>
                <span className="text-xs text-white/40 w-12 text-right font-mono">
                    {data.house ? `${isSpanish ? 'Casa' : 'House'} ${data.house}` : ''}
                </span>
            </div>
        </div>
    );
};

// Main NatalChart Component
const NatalChart = ({ isOpen, onClose, onOpenOracle, userData }) => {
    const { language } = useLanguage();
    const isSpanish = language === 'es';
    const chartRef = useRef(null);
    
    const [chartData, setChartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAspects, setShowAspects] = useState(true);
    const [expandedSection, setExpandedSection] = useState('planets');
    const [showShareModal, setShowShareModal] = useState(false);
    const [downloading, setDownloading] = useState(false);
    
    // Fetch chart data
    const fetchChart = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const headers = await getAuthHeaders();
            
            const profileRes = await fetch(`${API_URL}/api/profile`, {
                headers,
                credentials: 'include'
            });
            
            if (!profileRes.ok) throw new Error('Could not load profile');
            
            const profileData = await profileRes.json();
            const profile = profileData.profile || profileData;
            
            if (!profile.birth_date) {
                setError(isSpanish 
                    ? 'Agrega tu fecha de nacimiento en Configuración para ver tu carta natal'
                    : 'Add your birth date in Settings to see your natal chart');
                setLoading(false);
                return;
            }
            
            const chartRes = await fetch(`${API_URL}/api/calculate-chart`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    birth_date: profile.birth_date,
                    birth_time: profile.birth_time || '12:00',
                    lat: profile.location_lat || profile.birth_lat || profile.location?.lat || 0,
                    lng: profile.location_lng || profile.birth_lng || profile.location?.lng || 0,
                    birth_location: profile.location || profile.location?.display || profile.birth_place || '',
                    zodiac_system: profile.zodiac_system || 'tropical'
                })
            });
            
            if (!chartRes.ok) throw new Error('Chart calculation failed');
            
            const data = await chartRes.json();
            setChartData({
                ...data,
                profile,
                birthTimeKnown: !!profile.birth_time
            });
            
        } catch (err) {
            console.error('[CHART] Error:', err);
            setError(err.message || (isSpanish ? 'Error al calcular la carta' : 'Error calculating chart'));
        } finally {
            setLoading(false);
        }
    }, [isSpanish]);
    
    useEffect(() => {
        if (isOpen) {
            fetchChart();
        }
    }, [isOpen, fetchChart]);
    
    // Generate shareable chart image with Big Three and branding
    const generateShareableImage = async () => {
        if (!chartRef.current || !chartData) return null;
        
        try {
            const svgElement = chartRef.current.querySelector('svg');
            if (!svgElement) return null;
            
            const svgData = new XMLSerializer().serializeToString(svgElement);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Create a taller canvas for header and footer
            canvas.width = 800;
            canvas.height = 1000;
            
            // Background gradient
            const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
            gradient.addColorStop(0, '#0a0a0f');
            gradient.addColorStop(0.5, '#08080c');
            gradient.addColorStop(1, '#040406');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            
            // Add decorative stars
            ctx.fillStyle = '#d4af37';
            for (let i = 0; i < 50; i++) {
                const x = Math.random() * canvas.width;
                const y = Math.random() * canvas.height;
                const size = Math.random() * 1.5 + 0.5;
                ctx.globalAlpha = Math.random() * 0.5 + 0.2;
                ctx.beginPath();
                ctx.arc(x, y, size, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            
            // Header - User name and "Birth Chart"
            const userName = userData?.name || chartData?.profile?.name || (isSpanish ? 'Mi Carta' : 'My Chart');
            ctx.fillStyle = '#d4af37';
            ctx.font = 'bold 32px Cinzel, serif';
            ctx.textAlign = 'center';
            ctx.shadowColor = '#d4af37';
            ctx.shadowBlur = 15;
            ctx.fillText(userName, canvas.width / 2, 50);
            ctx.shadowBlur = 0;
            
            ctx.fillStyle = '#888899';
            ctx.font = '14px Inter, sans-serif';
            ctx.fillText(isSpanish ? 'CARTA NATAL' : 'BIRTH CHART', canvas.width / 2, 75);
            
            // Big Three display
            const bigThree = chartData.big_three || {};
            const bigThreeY = 110;
            const boxWidth = 220;
            const boxSpacing = 25;
            const startX = (canvas.width - (boxWidth * 3 + boxSpacing * 2)) / 2;
            
            const bigThreeItems = [
                { label: isSpanish ? 'SOL' : 'SUN', value: bigThree.sun || '—', symbol: '☉', color: '#FFD700' },
                { label: isSpanish ? 'LUNA' : 'MOON', value: bigThree.moon || '—', symbol: '☽', color: '#C0C0C0' },
                { label: isSpanish ? 'ASCENDENTE' : 'RISING', value: bigThree.rising || '—', symbol: '↑', color: '#d4af37' }
            ];
            
            bigThreeItems.forEach((item, i) => {
                const x = startX + i * (boxWidth + boxSpacing);
                
                // Box background
                ctx.fillStyle = 'rgba(212, 175, 55, 0.08)';
                ctx.strokeStyle = 'rgba(212, 175, 55, 0.3)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.roundRect(x, bigThreeY, boxWidth, 60, 10);
                ctx.fill();
                ctx.stroke();
                
                // Label
                ctx.fillStyle = '#666677';
                ctx.font = '10px Inter, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(item.label, x + boxWidth / 2, bigThreeY + 18);
                
                // Symbol and sign
                ctx.fillStyle = item.color;
                ctx.font = '24px serif';
                ctx.fillText(item.symbol, x + boxWidth / 2 - 40, bigThreeY + 45);
                
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 16px Inter, sans-serif';
                ctx.fillText(item.value, x + boxWidth / 2 + 10, bigThreeY + 45);
            });
            
            // Load and draw chart SVG
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    // Draw chart centered below Big Three
                    const chartY = 190;
                    const chartSize = 520;
                    const chartX = (canvas.width - chartSize) / 2;
                    ctx.drawImage(img, chartX, chartY, chartSize, chartSize);
                    
                    // Element balance bar at bottom
                    const elementBalance = getElementBalance();
                    if (elementBalance) {
                        const barY = 730;
                        const barWidth = 600;
                        const barX = (canvas.width - barWidth) / 2;
                        
                        ctx.fillStyle = '#444455';
                        ctx.font = '10px Inter, sans-serif';
                        ctx.textAlign = 'center';
                        ctx.fillText(isSpanish ? 'BALANCE ELEMENTAL' : 'ELEMENTAL BALANCE', canvas.width / 2, barY);
                        
                        const total = elementBalance.reduce((sum, e) => sum + e.count, 0);
                        let offsetX = barX;
                        elementBalance.forEach(({ element, count }) => {
                            const width = (count / total) * barWidth;
                            ctx.fillStyle = ELEMENT_COLORS[element];
                            ctx.globalAlpha = 0.7;
                            ctx.fillRect(offsetX, barY + 10, width, 8);
                            ctx.globalAlpha = 1;
                            offsetX += width;
                        });
                        
                        // Element labels
                        ctx.font = '9px Inter, sans-serif';
                        const labelY = barY + 35;
                        const labelSpacing = barWidth / 4;
                        ['Fire', 'Earth', 'Air', 'Water'].forEach((el, i) => {
                            const elData = elementBalance.find(e => e.element === el);
                            ctx.fillStyle = ELEMENT_COLORS[el];
                            ctx.fillText(
                                `${isSpanish ? {Fire:'Fuego',Earth:'Tierra',Air:'Aire',Water:'Agua'}[el] : el}: ${elData?.count || 0}`,
                                barX + labelSpacing * i + labelSpacing / 2,
                                labelY
                            );
                        });
                    }
                    
                    // Footer branding
                    ctx.fillStyle = '#d4af37';
                    ctx.font = 'bold 18px Cinzel, serif';
                    ctx.textAlign = 'center';
                    ctx.shadowColor = '#d4af37';
                    ctx.shadowBlur = 10;
                    ctx.fillText('ASTRAL BELONGING', canvas.width / 2, canvas.height - 45);
                    ctx.shadowBlur = 0;
                    
                    ctx.fillStyle = '#666677';
                    ctx.font = '11px Inter, sans-serif';
                    ctx.fillText(isSpanish ? 'Donde los Cielos Pronuncian tu Nombre' : 'Where the Heavens Speak Your Name', canvas.width / 2, canvas.height - 25);
                    
                    resolve(canvas.toDataURL('image/png'));
                };
                img.onerror = () => resolve(null);
                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
            });
        } catch (err) {
            console.error('[CHART] Generate shareable image error:', err);
            return null;
        }
    };
    
    // Download as PNG (shareable version)
    const handleDownload = async () => {
        if (!chartRef.current) return;
        setDownloading(true);
        
        try {
            const imageDataUrl = await generateShareableImage();
            if (imageDataUrl) {
                const link = document.createElement('a');
                link.download = `astral-belonging-chart-${Date.now()}.png`;
                link.href = imageDataUrl;
                link.click();
            }
        } catch (err) {
            console.error('[CHART] Download error:', err);
        } finally {
            setDownloading(false);
        }
    };
    
    // Share chart with image
    const handleShareWithImage = async () => {
        setDownloading(true);
        try {
            const imageDataUrl = await generateShareableImage();
            if (imageDataUrl && navigator.share && navigator.canShare) {
                // Convert data URL to blob for native share
                const response = await fetch(imageDataUrl);
                const blob = await response.blob();
                const file = new File([blob], 'astral-chart.png', { type: 'image/png' });
                
                if (navigator.canShare({ files: [file] })) {
                    await navigator.share({
                        title: isSpanish ? 'Mi Carta Natal' : 'My Birth Chart',
                        text: chartData?.big_three 
                            ? (isSpanish 
                                ? `☉ Sol en ${chartData.big_three.sun || '?'} | ☽ Luna en ${chartData.big_three.moon || '?'} | ↑ Asc ${chartData.big_three.rising || '?'}`
                                : `☉ Sun in ${chartData.big_three.sun || '?'} | ☽ Moon in ${chartData.big_three.moon || '?'} | ↑ Rising ${chartData.big_three.rising || '?'}`)
                            : '',
                        files: [file]
                    });
                    return;
                }
            }
            // Fallback to social share modal
            setShowShareModal(true);
        } catch (err) {
            console.error('[CHART] Share error:', err);
            setShowShareModal(true);
        } finally {
            setDownloading(false);
        }
    };
    
    // Open Oracle with chart context
    const handleOpenOracle = () => {
        if (onOpenOracle && chartData) {
            const bigThree = chartData.big_three || {};
            const context = isSpanish
                ? `Mi carta natal: Sol en ${bigThree.sun || '?'}, Luna en ${bigThree.moon || '?'}, Ascendente en ${bigThree.rising || '?'}. ¿Qué puedes decirme sobre mi carta?`
                : `My natal chart: Sun in ${bigThree.sun || '?'}, Moon in ${bigThree.moon || '?'}, Rising in ${bigThree.rising || '?'}. What can you tell me about my chart?`;
            
            onOpenOracle('cosmos', { chartContext: context, chartData });
        }
    };
    
    // Calculate element balance
    const getElementBalance = () => {
        if (!chartData?.planets) return null;
        
        const counts = { Fire: 0, Earth: 0, Air: 0, Water: 0 };
        Object.values(chartData.planets).forEach(planet => {
            const sign = ZODIAC_SIGNS.find(s => s.name === planet.sign);
            if (sign) counts[sign.element]++;
        });
        
        const total = Object.values(counts).reduce((a, b) => a + b, 0);
        return Object.entries(counts).map(([element, count]) => ({
            element,
            count,
            percentage: total > 0 ? Math.round((count / total) * 100) : 0
        }));
    };
    
    if (!isOpen) return null;
    
    const bigThree = chartData?.big_three || {};
    const elementBalance = getElementBalance();
    
    return (
        <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            <motion.div 
                className="absolute inset-0 bg-black/95 backdrop-blur-md"
                onClick={onClose}
            />
            
            <motion.div
                className="relative w-full max-w-2xl max-h-[95vh] overflow-y-auto rounded-2xl"
                style={{
                    background: 'linear-gradient(145deg, rgba(10,10,12,0.99) 0%, rgba(5,5,8,1) 100%)',
                    border: '1px solid rgba(212,175,55,0.3)',
                    boxShadow: '0 25px 80px rgba(0,0,0,0.8), 0 0 60px rgba(212,175,55,0.1)'
                }}
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.95, y: 20 }}
                onClick={e => e.stopPropagation()}
                data-testid="natal-chart-modal"
            >
                {/* Header */}
                <div className="sticky top-0 z-10 px-5 py-4 border-b border-white/10 bg-black/90 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[9px] font-inter uppercase tracking-[0.2em] text-white/40 mb-1">
                                {isSpanish ? 'Carta Natal' : 'Birth Chart'}
                            </p>
                            <h2 className="text-xl font-cinzel text-primary">
                                {userData?.name || (isSpanish ? 'Tu Carta' : 'Your Chart')}
                            </h2>
                        </div>
                        <button 
                            onClick={onClose}
                            className="p-2 rounded-full hover:bg-white/10 transition-colors"
                        >
                            <X size={20} className="text-white/60" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-5">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <motion.div
                                className="text-4xl"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                            >
                                ✧
                            </motion.div>
                            <p className="mt-4 text-sm text-white/40 font-inter">
                                {isSpanish ? 'Calculando tu carta natal...' : 'Calculating your birth chart...'}
                            </p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <Star size={40} className="mx-auto text-white/20 mb-4" />
                            <p className="text-sm text-white/60 mb-4">{error}</p>
                            <button 
                                onClick={fetchChart}
                                className="px-4 py-2 text-xs text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
                            >
                                <RefreshCw size={14} className="inline mr-2" />
                                {isSpanish ? 'Reintentar' : 'Retry'}
                            </button>
                        </div>
                    ) : chartData ? (
                        <div className="space-y-6">
                            {/* Big Three Header */}
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { label: isSpanish ? 'Sol' : 'Sun', value: bigThree.sun, symbol: '☉', color: '#FFD700' },
                                    { label: isSpanish ? 'Luna' : 'Moon', value: bigThree.moon, symbol: '☽', color: '#C0C0C0' },
                                    { label: isSpanish ? 'Ascendente' : 'Rising', value: bigThree.rising, symbol: '↑', color: '#D4AF37' }
                                ].map(item => {
                                    const signData = ZODIAC_SIGNS.find(s => s.name === item.value);
                                    return (
                                        <div 
                                            key={item.label}
                                            className="p-3 rounded-xl text-center"
                                            style={{
                                                background: `linear-gradient(135deg, ${item.color}15 0%, transparent 100%)`,
                                                border: `1px solid ${item.color}40`
                                            }}
                                        >
                                            <p className="text-[9px] text-white/40 uppercase tracking-wider mb-1">{item.label}</p>
                                            <p className="text-2xl font-serif" style={{ color: signData?.color || item.color }}>
                                                {signData?.symbol || '?'}
                                            </p>
                                            <p className="text-xs text-white/70 mt-1">{item.value || '—'}</p>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Birth time warning */}
                            {!chartData.birthTimeKnown && (
                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                                    <p className="text-xs text-amber-300">
                                        <Info size={12} className="inline mr-1" />
                                        {isSpanish 
                                            ? 'Carta aproximada. Agrega tu hora de nacimiento para casas y ascendente precisos.'
                                            : 'Approximate chart. Add your birth time for accurate houses and ascendant.'}
                                    </p>
                                </div>
                            )}

                            {/* Chart Wheel */}
                            <div 
                                ref={chartRef}
                                className="relative rounded-xl overflow-hidden"
                            >
                                <ProfessionalChartWheel chartData={chartData} showAspects={showAspects} size={420} />
                                
                                {/* Toggle aspects button */}
                                <button
                                    onClick={() => setShowAspects(!showAspects)}
                                    className="absolute top-3 right-3 p-2 rounded-lg bg-black/70 hover:bg-black/90 transition-colors flex items-center gap-2"
                                    title={showAspects ? 'Hide aspects' : 'Show aspects'}
                                >
                                    {showAspects ? <Eye size={14} className="text-blue-400" /> : <EyeOff size={14} className="text-white/40" />}
                                    <span className="text-[10px] text-white/60">{isSpanish ? 'Aspectos' : 'Aspects'}</span>
                                </button>
                            </div>

                            {/* Element Balance */}
                            {elementBalance && (
                                <div>
                                    <p className="text-[9px] text-white/40 uppercase tracking-wider mb-2">
                                        {isSpanish ? 'Balance Elemental' : 'Element Balance'}
                                    </p>
                                    <div className="flex gap-2">
                                        {elementBalance.map(({ element, count, percentage }) => (
                                            <div 
                                                key={element}
                                                className="flex-1 p-3 rounded-lg text-center"
                                                style={{ 
                                                    background: `${ELEMENT_COLORS[element]}10`,
                                                    border: `1px solid ${ELEMENT_COLORS[element]}30`
                                                }}
                                            >
                                                <p className="text-xl font-cinzel" style={{ color: ELEMENT_COLORS[element] }}>{count}</p>
                                                <p className="text-[9px] text-white/50">{isSpanish ? {Fire: 'Fuego', Earth: 'Tierra', Air: 'Aire', Water: 'Agua'}[element] : element}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Expandable Sections */}
                            <div className="space-y-2">
                                {/* Planets Section */}
                                <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                    <button
                                        onClick={() => setExpandedSection(expandedSection === 'planets' ? null : 'planets')}
                                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                                    >
                                        <span className="text-sm text-white/80 font-inter">
                                            {isSpanish ? 'Planetas en Signos' : 'Planets in Signs'}
                                        </span>
                                        {expandedSection === 'planets' ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
                                    </button>
                                    <AnimatePresence>
                                        {expandedSection === 'planets' && chartData.planets && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                className="px-2 pb-2"
                                            >
                                                {Object.entries(chartData.planets).map(([planet, data]) => (
                                                    <PlanetRow key={planet} planet={planet} data={data} isSpanish={isSpanish} />
                                                ))}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Houses Section */}
                                {chartData.houses && (
                                    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <button
                                            onClick={() => setExpandedSection(expandedSection === 'houses' ? null : 'houses')}
                                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                                        >
                                            <span className="text-sm text-white/80 font-inter">
                                                {isSpanish ? 'Cúspides de Casas' : 'House Cusps'}
                                            </span>
                                            {expandedSection === 'houses' ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
                                        </button>
                                        <AnimatePresence>
                                            {expandedSection === 'houses' && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-4 pb-3 grid grid-cols-2 gap-2"
                                                >
                                                    {chartData.houses.map((house, i) => {
                                                        const signData = ZODIAC_SIGNS.find(s => s.name === house.sign);
                                                        const degree = Math.floor((house.longitude || 0) % 30);
                                                        return (
                                                            <div key={i} className="flex items-center justify-between py-1 px-2 rounded bg-white/5">
                                                                <span className="text-xs text-white/50 font-mono">
                                                                    {isSpanish ? 'Casa' : 'House'} {i + 1}
                                                                </span>
                                                                <span className="text-xs flex items-center gap-1" style={{ color: signData?.color || '#999' }}>
                                                                    {degree}° <span className="font-serif">{signData?.symbol}</span> {house.sign}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                                
                                {/* Aspects Section */}
                                {chartData.aspects && chartData.aspects.length > 0 && (
                                    <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                        <button
                                            onClick={() => setExpandedSection(expandedSection === 'aspects' ? null : 'aspects')}
                                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition-colors"
                                        >
                                            <span className="text-sm text-white/80 font-inter">
                                                {isSpanish ? 'Aspectos Planetarios' : 'Planetary Aspects'}
                                            </span>
                                            {expandedSection === 'aspects' ? <ChevronUp size={16} className="text-white/40" /> : <ChevronDown size={16} className="text-white/40" />}
                                        </button>
                                        <AnimatePresence>
                                            {expandedSection === 'aspects' && (
                                                <motion.div
                                                    initial={{ height: 0, opacity: 0 }}
                                                    animate={{ height: 'auto', opacity: 1 }}
                                                    exit={{ height: 0, opacity: 0 }}
                                                    className="px-2 pb-2 max-h-64 overflow-y-auto"
                                                >
                                                    {chartData.aspects.map((aspect, i) => {
                                                        const p1 = PLANET_DATA[aspect.planet1] || { symbol: '?', color: '#999' };
                                                        const p2 = PLANET_DATA[aspect.planet2] || { symbol: '?', color: '#999' };
                                                        const aspectConfig = ASPECT_CONFIG[aspect.aspect] || {};
                                                        return (
                                                            <div key={i} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/5 transition-colors">
                                                                <div className="flex items-center gap-2">
                                                                    <span style={{ color: p1.color }} className="text-lg font-serif">{p1.symbol}</span>
                                                                    <span style={{ color: aspectConfig.color || '#888' }} className="text-xs px-1.5 py-0.5 rounded" 
                                                                          title={aspect.aspect}>
                                                                        {aspectConfig.symbol || '○'}
                                                                    </span>
                                                                    <span style={{ color: p2.color }} className="text-lg font-serif">{p2.symbol}</span>
                                                                </div>
                                                                <div className="flex items-center gap-3">
                                                                    <span className="text-xs text-white/50 capitalize">{aspect.aspect}</span>
                                                                    <span className="text-xs text-white/30 font-mono">{aspect.orb?.toFixed(1) || '0.0'}°</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-4 border-t border-white/10">
                                {/* Ask Oracle */}
                                <button
                                    onClick={handleOpenOracle}
                                    className="flex-1 py-3 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.01]"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(147,112,219,0.2) 0%, rgba(147,112,219,0.1) 100%)',
                                        border: '1px solid rgba(147,112,219,0.4)'
                                    }}
                                    data-testid="ask-oracle-btn"
                                >
                                    <MessageCircle size={16} style={{ color: '#9370DB' }} />
                                    <span className="text-sm" style={{ color: '#9370DB' }}>
                                        {isSpanish ? 'Pregunta al Cosmos' : 'Ask the Oracle'}
                                    </span>
                                </button>
                                
                                {/* Share with Image - Enhanced */}
                                <button
                                    onClick={handleShareWithImage}
                                    disabled={downloading}
                                    className="flex-1 py-3 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-50"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)',
                                        border: '1px solid rgba(212,175,55,0.4)'
                                    }}
                                    data-testid="share-chart-btn"
                                >
                                    <Share2 size={16} className="text-primary" />
                                    <span className="text-sm text-primary">
                                        {isSpanish ? 'Compartir Carta' : 'Share Chart'}
                                    </span>
                                </button>
                                
                                {/* Download */}
                                <button
                                    onClick={handleDownload}
                                    disabled={downloading}
                                    className="py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-50"
                                    style={{
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)'
                                    }}
                                    data-testid="download-chart-btn"
                                >
                                    <Download size={16} className="text-white/60" />
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </motion.div>
            
            {/* Social Share Modal */}
            <SocialShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                shareTitle={isSpanish ? 'Mi Carta Natal' : 'My Birth Chart'}
                shareText={chartData?.big_three 
                    ? (isSpanish 
                        ? `☉ Sol en ${chartData.big_three.sun || '?'} | ☽ Luna en ${chartData.big_three.moon || '?'} | ↑ Asc ${chartData.big_three.rising || '?'} - Astral Belonging`
                        : `☉ Sun in ${chartData.big_three.sun || '?'} | ☽ Moon in ${chartData.big_three.moon || '?'} | ↑ Rising ${chartData.big_three.rising || '?'} - Astral Belonging`)
                    : ''}
                shareUrl={window.location.origin}
                onDownload={handleDownload}
                language={language}
            />
        </motion.div>
    );
};

export default NatalChart;
